import { z } from 'zod';
import { BaseResponseSchema, containerSlotSchema, emailSchema, integerIdSchema, } from './primitives.js';
import { gatewaySerialNumberParamSchema } from './params.js';
import { onlineOfflineStatusSchema } from './efem-core.schemas.js';
// ========================================
// WAFER TRANSACTION EFEM INTEGRATION
// ========================================
// Base load port schema (1-4)
export const loadPortIdSchema = z.coerce.number().int().min(1).max(4);
// Single wafer transfer request
export const WaferTransferEfemRequestSchema = z.object({
    fromLoadPort: loadPortIdSchema,
    fromSlot: containerSlotSchema,
    fromContainerId: integerIdSchema,
    toLoadPort: loadPortIdSchema,
    toSlot: containerSlotSchema,
    toContainerId: integerIdSchema,
    comment: z.string().max(500).trim().optional(),
}).refine(data => !(data.fromLoadPort === data.toLoadPort && data.fromSlot === data.toSlot), { message: "Source and destination slot cannot be the same" });
// Single wafer transfer response
export const WaferTransferEfemResponseDataSchema = z.object({
    transferId: z.string(),
    fromLoadPort: loadPortIdSchema,
    fromSlot: containerSlotSchema,
    toLoadPort: loadPortIdSchema,
    toSlot: containerSlotSchema,
    timestamp: z.string(),
    duration: z.number().int().min(0),
});
export const WaferTransferEfemResponseSchema = BaseResponseSchema.extend({
    data: WaferTransferEfemResponseDataSchema,
});
// Individual transfer in a batch
export const BatchTransferItemSchema = z.object({
    fromLoadPort: loadPortIdSchema,
    fromSlot: containerSlotSchema,
    fromContainerId: integerIdSchema,
    toLoadPort: loadPortIdSchema,
    toSlot: containerSlotSchema,
    toContainerId: integerIdSchema,
});
// Batch transfer request
export const BatchWaferTransferEfemRequestSchema = z.object({
    transfers: z.array(BatchTransferItemSchema).min(1).max(25),
    comment: z.string().max(500).trim().optional(),
}).refine(data => {
    // Check for duplicate source slots
    const sourceSlotsSet = new Set();
    for (const transfer of data.transfers) {
        const key = `${transfer.fromLoadPort}-${transfer.fromSlot}`;
        if (sourceSlotsSet.has(key))
            return false;
        sourceSlotsSet.add(key);
    }
    return true;
}, { message: "Duplicate source slots are not allowed" }).refine(data => {
    // Check for duplicate target slots
    const targetSlotsSet = new Set();
    for (const transfer of data.transfers) {
        const key = `${transfer.toLoadPort}-${transfer.toSlot}`;
        if (targetSlotsSet.has(key))
            return false;
        targetSlotsSet.add(key);
    }
    return true;
}, { message: "Duplicate target slots are not allowed" });
// Batch transfer status enum
export const batchTransferStatusSchema = z.enum(['pending', 'in_progress', 'completed', 'failed']);
// Batch transfer response
export const BatchWaferTransferEfemResponseDataSchema = z.object({
    jobId: z.string(),
    totalTransfers: z.number().int().min(1),
    status: batchTransferStatusSchema,
    timestamp: z.string(),
});
export const BatchWaferTransferEfemResponseSchema = BaseResponseSchema.extend({
    data: BatchWaferTransferEfemResponseDataSchema,
});
// Type exports for wafer transaction
// ========================================
// EFEM Lock Management Schemas
// ========================================
// Lock operation types
export const efemLockOperationSchema = z.enum([
    'general',
    'scanning',
    'transfer',
    'imaging',
    'load',
    'unload'
]);
// Acquire lock request
export const AcquireEfemLockRequestSchema = z.object({
    operation: efemLockOperationSchema,
});
// Lock data in response
export const EfemLockDataSchema = z.object({
    userId: integerIdSchema,
    userEmail: emailSchema,
    sessionId: z.string(),
    operation: efemLockOperationSchema,
    acquiredAt: z.string(),
    expiresAt: z.string(),
    lastExtended: z.string().optional(),
});
// Acquire lock response
export const AcquireEfemLockResponseDataSchema = z.object({
    gatewaySerialNumber: gatewaySerialNumberParamSchema,
    userId: integerIdSchema,
    operation: efemLockOperationSchema,
    expiresAt: z.string(),
});
export const AcquireEfemLockResponseSchema = BaseResponseSchema.extend({
    data: AcquireEfemLockResponseDataSchema.optional(),
    lockedBy: z.object({
        email: emailSchema,
        userId: integerIdSchema,
        since: z.string(),
        operation: efemLockOperationSchema,
    }).optional(),
});
// Release lock response
export const ReleaseEfemLockResponseSchema = BaseResponseSchema;
// Extend lock response
export const ExtendEfemLockResponseDataSchema = z.object({
    expiresAt: z.string(),
});
export const ExtendEfemLockResponseSchema = BaseResponseSchema.extend({
    data: ExtendEfemLockResponseDataSchema.optional(),
});
// Lock status response
export const EfemLockStatusDataSchema = z.object({
    isLocked: z.boolean(),
    lockedBy: emailSchema.optional(),
    lockedByUserId: integerIdSchema.optional(),
    operation: efemLockOperationSchema.optional(),
    acquiredAt: z.string().optional(),
    expiresAt: z.string().optional(),
});
export const EfemLockStatusResponseSchema = BaseResponseSchema.extend({
    data: EfemLockStatusDataSchema,
});
// Force release lock request (admin only)
export const ForceReleaseEfemLockResponseDataSchema = z.object({
    previousOwner: emailSchema.optional(),
});
export const ForceReleaseEfemLockResponseSchema = BaseResponseSchema.extend({
    data: ForceReleaseEfemLockResponseDataSchema,
});
// EFEM client with lock status
export const EfemClientWithLockStatusSchema = z.object({
    gatewaySerialNumber: gatewaySerialNumberParamSchema.nullable(),
    name: z.string(),
    status: onlineOfflineStatusSchema,
    isActive: z.boolean(),
    lastSeen: z.string().optional(),
    createdAt: z.string(),
    lockStatus: EfemLockStatusDataSchema.optional(),
    transferEligible: z.boolean().optional(),
    slotScanEligible: z.boolean().optional(),
});
export const GetEfemClientsWithLocksResponseDataSchema = z.object({
    clients: z.array(EfemClientWithLockStatusSchema),
});
export const GetEfemClientsWithLocksResponseSchema = BaseResponseSchema.extend({
    data: GetEfemClientsWithLocksResponseDataSchema,
});
//# sourceMappingURL=efem-transactions.schemas.js.map